import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { PortfolioReportDTO } from '@inversor-hub/types';
import { useGetMe } from '@/lib/api/me';
import { useGetPortfolioReport } from '@/lib/api/investment-transactions';
import { formatCurrency } from '@/utils/misc';
import {
  paymentMethodLabels,
  transactionCategoryLabels,
  transactionTypeLabels,
} from '@/features/investment-transactions/constants';
import { exportPortfolioReport } from '@/features/investments/portfolio/exportPortfolioReport';
import { getInvestmentCountryLabel } from '@/features/investments/countries';

const ALL_INVESTMENTS = 'all';
const monthFormatter = new Intl.DateTimeFormat('es-ES', { month: 'short' });

const getMonthName = (year: number, month: number) =>
  monthFormatter.format(new Date(Date.UTC(year, month - 1, 1)));

const NetValue = ({ value }: { value: number }) => (
  <Typography
    component="span"
    fontWeight={700}
    color={value > 0 ? 'success.main' : value < 0 ? 'error.main' : 'text.primary'}
    sx={{ whiteSpace: 'nowrap' }}
  >
    {value > 0 ? '+' : ''}
    {formatCurrency(value)}
  </Typography>
);

const TotalsCard = ({
  title,
  value,
  icon,
  color,
  caption,
}: {
  title: string;
  value: number;
  icon: ReactNode;
  color?: 'success.main' | 'error.main' | 'text.primary';
  caption?: string;
}) => (
  <Card sx={{ height: '100%', boxShadow: 1 }}>
    <CardContent sx={{ p: 2.25, '&:last-child': { pb: 2.25 } }}>
      <Stack direction="row" spacing={1} alignItems="center" mb={1.25}>
        {icon}
        <Typography variant="body2" color="text.secondary" fontWeight={500}>
          {title}
        </Typography>
      </Stack>
      <Typography
        variant="h5"
        fontWeight={700}
        color={color}
        sx={{ whiteSpace: 'nowrap', lineHeight: 1.2 }}
      >
        {formatCurrency(value)}
      </Typography>
      {caption && (
        <Typography variant="caption" color="text.secondary" display="block" mt={0.75}>
          {caption}
        </Typography>
      )}
    </CardContent>
  </Card>
);

export default function PortfolioReportPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: user, isLoading: isUserLoading } = useGetMe();
  const [selectedYear, setSelectedYear] = useState<number | undefined>();
  const [selectedCountry, setSelectedCountry] = useState<string | undefined>();
  const {
    data: report,
    isLoading,
    isError,
    isPlaceholderData,
  } = useGetPortfolioReport(selectedYear, selectedCountry);
  const selectedInvestmentId = searchParams.get('investmentId') ?? ALL_INVESTMENTS;

  useEffect(() => {
    if (report && !isPlaceholderData && selectedYear === undefined) setSelectedYear(report.year);
  }, [isPlaceholderData, report, selectedYear]);

  useEffect(() => {
    if (report && !isPlaceholderData && selectedCountry === undefined) {
      setSelectedCountry(report.country);
    }
  }, [isPlaceholderData, report, selectedCountry]);

  const selectedInvestment = useMemo(
    () =>
      selectedInvestmentId === ALL_INVESTMENTS
        ? undefined
        : report?.investments.find(
            (investment) => investment.investmentId === selectedInvestmentId
          ),
    [report, selectedInvestmentId]
  );

  useEffect(() => {
    if (
      report &&
      selectedInvestmentId !== ALL_INVESTMENTS &&
      !report.investments.some((investment) => investment.investmentId === selectedInvestmentId)
    ) {
      setSearchParams({}, { replace: true });
    }
  }, [report, selectedInvestmentId, setSearchParams]);

  const activeTotals = selectedInvestment?.totals ?? report?.totals;
  const activeMonths = selectedInvestment?.months ?? report?.months ?? [];
  const activeCategories = selectedInvestment?.expenseCategories ?? report?.expenseCategories ?? [];
  const maxCategoryAmount = Math.max(...activeCategories.map((category) => category.amount), 0);
  const exportReport = useMemo<PortfolioReportDTO | undefined>(() => {
    if (!report) return undefined;
    if (!selectedInvestment) return report;
    return {
      ...report,
      totals: selectedInvestment.totals,
      months: selectedInvestment.months,
      expenseCategories: selectedInvestment.expenseCategories,
      investments: [selectedInvestment],
    };
  }, [report, selectedInvestment]);

  const handleInvestmentChange = (investmentId: string) => {
    if (investmentId === ALL_INVESTMENTS) {
      setSearchParams({});
      return;
    }
    setSearchParams({ investmentId });
  };

  const handleCountryChange = (country: string) => {
    setSelectedCountry(country);
    setSelectedYear(undefined);
    setSearchParams({});
  };

  if (isUserLoading) return <Skeleton variant="rectangular" height={320} />;
  if (user?.role !== 'investor') return <Navigate to="/inversiones" replace />;

  return (
    <Box p={{ xs: 2, md: 3 }} maxWidth={1440} mx="auto">
      <Stack spacing={2.5} mb={3}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', sm: 'flex-start' }}
          spacing={2}
        >
          <Stack direction="row" spacing={1.25} alignItems="flex-start">
            <IconButton
              aria-label="Volver al dashboard"
              onClick={() => navigate('/dashboard')}
              sx={{ mt: 0.25 }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h4" fontWeight={700}>
                Informe financiero
              </Typography>
              <Typography color="text.secondary">
                Analiza toda la cartera o selecciona un inmueble para ver su desglose.
              </Typography>
            </Box>
          </Stack>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            disabled={!exportReport || exportReport.investments.length === 0}
            onClick={() => exportReport && exportPortfolioReport(exportReport)}
            sx={{ whiteSpace: 'nowrap', minHeight: 40, flexShrink: 0 }}
          >
            Exportar XLSX
          </Button>
        </Stack>

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.25}
          sx={{ pl: { xs: 0, md: 6.25 } }}
        >
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 180 } }}>
            <InputLabel id="portfolio-country-label">País</InputLabel>
            <Select
              labelId="portfolio-country-label"
              label="País"
              value={selectedCountry ?? report?.country ?? ''}
              onChange={(event) => handleCountryChange(event.target.value)}
            >
              {report?.availableCountries.map((country) => (
                <MenuItem key={country} value={country}>
                  {getInvestmentCountryLabel(country)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 260 } }}>
            <InputLabel id="portfolio-investment-label">Vista</InputLabel>
            <Select
              labelId="portfolio-investment-label"
              label="Vista"
              value={selectedInvestmentId}
              onChange={(event) => handleInvestmentChange(event.target.value)}
            >
              <MenuItem value={ALL_INVESTMENTS}>Toda la cartera</MenuItem>
              {report?.investments.map((investment) => (
                <MenuItem key={investment.investmentId} value={investment.investmentId}>
                  {investment.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 130 } }}>
            <InputLabel id="portfolio-year-label">Ejercicio</InputLabel>
            <Select
              labelId="portfolio-year-label"
              label="Ejercicio"
              value={selectedYear ?? report?.year ?? ''}
              onChange={(event) => setSelectedYear(Number(event.target.value))}
            >
              {(report?.availableYears.length ? report.availableYears : [report?.year])
                .filter((year): year is number => year !== undefined)
                .map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Stack>
      </Stack>

      {isError && (
        <Alert severity="error">
          No se ha podido generar el informe. Inténtalo de nuevo en unos minutos.
        </Alert>
      )}

      {isLoading || !report || !activeTotals ? (
        <Grid container spacing={2}>
          {Array.from({ length: 4 }, (_, index) => (
            <Grid item xs={12} sm={6} lg={3} key={index}>
              <Skeleton variant="rectangular" height={130} />
            </Grid>
          ))}
        </Grid>
      ) : report.investments.length === 0 ? (
        <Alert severity="info">
          Registra al menos un inmueble para poder generar el informe de cartera.
        </Alert>
      ) : (
        <>
          {activeTotals.uncategorizedCount > 0 && (
            <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 3 }}>
              Hay {activeTotals.uncategorizedCount}{' '}
              {activeTotals.uncategorizedCount === 1 ? 'movimiento' : 'movimientos'} pendiente
              {activeTotals.uncategorizedCount === 1 ? '' : 's'} de clasificar. El neto está
              incluido, pero el desglose por categoría puede estar incompleto.
            </Alert>
          )}

          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} sm={6} lg={3}>
              <TotalsCard
                title="Ingresos"
                value={activeTotals.income}
                icon={<TrendingUpIcon color="success" />}
                color="success.main"
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <TotalsCard
                title="Gastos"
                value={activeTotals.expenses}
                icon={<TrendingDownIcon color="error" />}
                color="error.main"
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <TotalsCard
                title="Neto del ejercicio"
                value={activeTotals.net}
                icon={<AccountBalanceWalletIcon color="primary" />}
                color={activeTotals.net >= 0 ? 'success.main' : 'error.main'}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <TotalsCard
                title="Neto mensual medio"
                value={activeTotals.monthlyAverageNet}
                icon={<AccountBalanceWalletIcon color="primary" />}
                caption="Neto anual dividido entre 12 meses"
              />
            </Grid>
          </Grid>

          <Card sx={{ mb: 3, boxShadow: 1 }}>
            <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
              <Typography variant="h6" fontWeight={700}>
                Evolución mensual
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2.5}>
                Ingresos, gastos y resultado neto registrado en cada mes.
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: 'repeat(2, minmax(0, 1fr))',
                    sm: 'repeat(3, minmax(0, 1fr))',
                    lg: 'repeat(6, minmax(0, 1fr))',
                  },
                  gap: 1.5,
                }}
              >
                {activeMonths.map((month) => (
                  <Box
                    key={month.month}
                    sx={{
                      minWidth: 0,
                      p: 1.5,
                      border: 1,
                      borderColor: month.net < 0 ? 'error.main' : 'divider',
                      borderRadius: 1.5,
                      bgcolor: 'background.paper',
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      textTransform="capitalize"
                      fontWeight={600}
                    >
                      {getMonthName(report.year, month.month)}
                    </Typography>
                    <Box my={0.75}>
                      <NetValue value={month.net} />
                    </Box>
                    <Stack direction="row" justifyContent="space-between" spacing={1}>
                      <Typography variant="caption" color="text.secondary">
                        Ingresos
                      </Typography>
                      <Typography variant="caption" color="success.main" fontWeight={600}>
                        {formatCurrency(month.income)}
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" spacing={1}>
                      <Typography variant="caption" color="text.secondary">
                        Gastos
                      </Typography>
                      <Typography variant="caption" color="error.main" fontWeight={600}>
                        {formatCurrency(month.expenses)}
                      </Typography>
                    </Stack>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>

          <Grid container spacing={3} mb={3}>
            {!selectedInvestment && (
              <Grid item xs={12} lg={8}>
                <Card sx={{ height: '100%', boxShadow: 1 }}>
                  <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                    <Typography variant="h6" fontWeight={700}>
                      Comparativa por inmueble
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                      Pulsa sobre un inmueble para abrir su desglose.
                    </Typography>
                    <TableContainer>
                      <Table
                        size="small"
                        sx={{
                          width: '100%',
                          tableLayout: 'fixed',
                          '& .MuiTableCell-root': {
                            px: { xs: 0.75, md: 1 },
                          },
                        }}
                      >
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ width: '38%' }}>Inmueble</TableCell>
                            <TableCell align="right" sx={{ width: '17%' }}>
                              Ingresos
                            </TableCell>
                            <TableCell align="right" sx={{ width: '17%' }}>
                              Gastos
                            </TableCell>
                            <TableCell align="right" sx={{ width: '18%' }}>
                              Neto
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                width: '18%',
                                display: { xs: 'none', xl: 'table-cell' },
                              }}
                            >
                              Media mensual
                            </TableCell>
                            <TableCell padding="none" sx={{ width: 28 }} />
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {report.investments.map((investment) => (
                            <TableRow
                              key={investment.investmentId}
                              hover
                              tabIndex={0}
                              role="button"
                              aria-label={`Ver detalle de ${investment.name}`}
                              onClick={() => handleInvestmentChange(investment.investmentId)}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                  handleInvestmentChange(investment.investmentId);
                                }
                              }}
                              sx={{ cursor: 'pointer' }}
                            >
                              <TableCell>
                                <Typography fontWeight={600} noWrap title={investment.name}>
                                  {investment.name}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                  noWrap
                                  title={`${investment.address}, ${investment.city}`}
                                >
                                  {investment.address}, {investment.city}
                                </Typography>
                              </TableCell>
                              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                {formatCurrency(investment.totals.income)}
                              </TableCell>
                              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                {formatCurrency(investment.totals.expenses)}
                              </TableCell>
                              <TableCell align="right">
                                <NetValue value={investment.totals.net} />
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{
                                  whiteSpace: 'nowrap',
                                  display: { xs: 'none', xl: 'table-cell' },
                                }}
                              >
                                {formatCurrency(investment.totals.monthlyAverageNet)}
                              </TableCell>
                              <TableCell padding="none">
                                <ChevronRightIcon color="action" fontSize="small" />
                              </TableCell>
                            </TableRow>
                          ))}
                          <TableRow sx={{ bgcolor: 'action.hover' }}>
                            <TableCell>
                              <Typography fontWeight={700}>Total cartera</Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                              {formatCurrency(report.totals.income)}
                            </TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                              {formatCurrency(report.totals.expenses)}
                            </TableCell>
                            <TableCell align="right">
                              <NetValue value={report.totals.net} />
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                whiteSpace: 'nowrap',
                                display: { xs: 'none', xl: 'table-cell' },
                              }}
                            >
                              {formatCurrency(report.totals.monthlyAverageNet)}
                            </TableCell>
                            <TableCell padding="none" />
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>
            )}

            <Grid item xs={12} lg={selectedInvestment ? 12 : 4}>
              <Card sx={{ height: '100%', boxShadow: 1 }}>
                <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                  <Typography variant="h6" fontWeight={700}>
                    Gastos por categoría
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    {selectedInvestment
                      ? `Distribución de gastos de ${selectedInvestment.name}.`
                      : 'Distribución agregada de toda la cartera.'}
                  </Typography>
                  {activeCategories.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No hay gastos registrados en {report.year}.
                    </Typography>
                  ) : (
                    <Stack spacing={2}>
                      {activeCategories.map((category) => (
                        <Box key={category.category}>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            spacing={2}
                            mb={0.75}
                          >
                            <Typography variant="body2">
                              {transactionCategoryLabels[category.category]}
                            </Typography>
                            <Stack direction="row" spacing={1} alignItems="baseline">
                              <Typography variant="caption" color="text.secondary">
                                {(category.percentage * 100).toLocaleString('es-ES', {
                                  maximumFractionDigits: 1,
                                })}
                                %
                              </Typography>
                              <Typography variant="body2" fontWeight={700} whiteSpace="nowrap">
                                {formatCurrency(category.amount)}
                              </Typography>
                            </Stack>
                          </Stack>
                          <LinearProgress
                            variant="determinate"
                            value={
                              maxCategoryAmount > 0
                                ? (category.amount / maxCategoryAmount) * 100
                                : 0
                            }
                            sx={{ height: 6, borderRadius: 999 }}
                            aria-label={`${transactionCategoryLabels[category.category]}: ${formatCurrency(category.amount)}`}
                          />
                        </Box>
                      ))}
                    </Stack>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {selectedInvestment && (
            <Card sx={{ boxShadow: 1 }}>
              <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  spacing={1}
                  mb={2}
                >
                  <Box>
                    <Typography variant="h6" fontWeight={700}>
                      Movimientos de {selectedInvestment.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedInvestment.totals.transactionCount} movimientos registrados en{' '}
                      {report.year}.
                    </Typography>
                  </Box>
                  {selectedInvestment.totals.uncategorizedCount > 0 && (
                    <Chip
                      size="small"
                      color="warning"
                      label={`${selectedInvestment.totals.uncategorizedCount} por revisar`}
                    />
                  )}
                </Stack>

                {selectedInvestment.transactions.length === 0 ? (
                  <Alert severity="info">
                    No hay movimientos registrados para este inmueble en {report.year}.
                  </Alert>
                ) : (
                  <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table size="small" sx={{ minWidth: 760 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell>Fecha</TableCell>
                          <TableCell>Concepto</TableCell>
                          <TableCell>Tipo</TableCell>
                          <TableCell>Categoría</TableCell>
                          <TableCell>Método</TableCell>
                          <TableCell align="right">Importe</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedInvestment.transactions.flatMap((transaction) =>
                          transaction.lines.map((line) => (
                            <TableRow key={`${transaction.id}-${line.id}`} hover>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                {new Date(transaction.date).toLocaleDateString('es-ES')}
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {transaction.concept ?? 'Sin concepto'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  color={transaction.type === 'INCOME' ? 'success' : 'default'}
                                  label={transactionTypeLabels[transaction.type]}
                                />
                              </TableCell>
                              <TableCell>
                                <Stack direction="row" spacing={0.75} alignItems="center">
                                  <Typography variant="body2">
                                    {transactionCategoryLabels[line.category]}
                                  </Typography>
                                  {line.category === 'REVIEW' && (
                                    <WarningAmberIcon color="warning" fontSize="small" />
                                  )}
                                </Stack>
                              </TableCell>
                              <TableCell>
                                {transaction.paymentMethod
                                  ? paymentMethodLabels[transaction.paymentMethod]
                                  : '—'}
                              </TableCell>
                              <TableCell align="right">
                                <Typography
                                  fontWeight={700}
                                  color={
                                    transaction.type === 'INCOME' ? 'success.main' : 'text.primary'
                                  }
                                  sx={{ whiteSpace: 'nowrap' }}
                                >
                                  {transaction.type === 'INCOME' ? '+' : '-'}
                                  {formatCurrency(line.amount)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}
    </Box>
  );
}
